/* @flow */

import * as React from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import { List as VirtualList, AutoSizer, CellMeasurerCache, CellMeasurer } from 'react-virtualized';

//----------------------------------------------------------------------//
// MATERIAL UI.                                                         //
//----------------------------------------------------------------------//

import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';

//----------------------------------------------------------------------//
// GLOBAL COMPONENTS                                                    //
//----------------------------------------------------------------------//

import { createNewLocation } from 'permissions/account';
import AddNewLink from 'components/AddNewLink/AddNewLink';
import finderColumns from 'components/FinderColumns/FinderColumns';
import Loading from 'components/Loading/Loading';
import type TypeAccountStore from 'stores/AccountStore.js';
import * as statuses from 'utils/statuses';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from './LocationColumn.module.scss';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import DropdownList from './DropdownList/DropdownList';

type TypeProps = {
  accountStore: TypeAccountStore,
  index: number,
  finderColumn: Object,
  expandable: boolean,
  blankText: string,
  linkText: string,
  placeholder: string,
  selectedItem: Object,
  pathChooser: string,
  onSelect: () => void,
};

@translate()
@inject('accountStore', 'locationStore', 'actionDrawerStore')
@finderColumns
@observer
export default class LocationColumn extends React.Component<*> {
  @observable expandedStatus = Object.keys(this.props.columnData[0])[0];

  @observable expandedLocation = null;

  virtualListRef = React.createRef();

  state = {
    listKey: 0,
    rowIndex: 0,
  };

  componentDidMount() {
    this.handleUpdateRowCache();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const index: Number = prevProps.actionDrawerStore.locationColumn;

    if (prevProps.actionDrawerStore.loadParent) {
      prevProps.accountStore.updateLocations(
        prevProps.accountStore.locationSelections[index],
        index,
        false
      );
      prevProps.actionDrawerStore.loadParent = false;
    }
  }

  handleUpdateRowCache() {
    this.virtualListRef.current = new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 50,
    });
  }

  handleExpandedChange = (panel: string) => (ev: SyntheticInputEvent<*>, expanded: boolean) => {
    this.expandedStatus = expanded ? panel : false;
  };

  handleExpandedLocationChange = (location: number, expanded: boolean) => {
    this.expandedLocation = expanded ? location : false;
  };

  renderExpansionPanelDetails(data: Array<*>, currentStatus: string): React.Element<*> {
    const columnName = this.props.pathChooser;
    const list = observable.box(data.length ? data : []);

    if (columnName === 'site') {
      return (
        <ExpansionPanelDetails className={styles['auto-sizer-wrapper']}>
          <List className={styles.locationList} component="ul" disablePadding={true}>
            {this.renderVirtualizedExpansionPanel(data, columnName)}
          </List>
        </ExpansionPanelDetails>
      );
    }

    return (
      <ExpansionPanelDetails className={styles['auto-sizer-wrapper']}>
        <List className={styles.locationList} component="ul" disablePadding={true}>
          {toJS(list).map((item: *, index: number) => {
            const selected: boolean =
              this.props.selectedItem && this.props.selectedItem.id === item.id;
            return (
              <DropdownList
                key={item.id}
                location={item}
                selected={selected}
                columnName={columnName}
                onExpandLocationChange={this.handleExpandedLocationChange}
                expandedLocation={this.expandedLocation}
                onClick={(): void => {
                  if (this.props.index < 5) {
                    this.props.locationUrlConfig.updatedId(this.props.pathChooser, data, item);
                    this.props.onSelect(item, this.props.index);
                  }
                }}
                {...this.props}
              />
            );
          })}
        </List>
      </ExpansionPanelDetails>
    );
  }

  renderExpansionPanelSummary(data: Array<*>, currentStatus: string): React.Element<*> {
    const { i18n } = this.props;

    return (
      <ExpansionPanelSummary
        className={styles['expansion-panel-header']}
        expandIcon={
          <svg className={styles['icon-chevron']}>
            <use
              xlinkHref={
                this.expandedStatus === currentStatus ? '#icon-chevron-down' : '#icon-chevron-up'
              }
            />
          </svg>
        }
      >
        <Typography>
          {`${statuses.translate(currentStatus, i18n)}
                         ${this.props.finderColumn.columnTitle}
                        (${data[0][currentStatus].length})`}
        </Typography>
      </ExpansionPanelSummary>
    );
  }

  renderVirtualizedExpansionPanel(data: Array<*>, columnName: string): React.Element<*> {
    if (this.virtualListRef.current === null) {
      this.handleUpdateRowCache();
    }

    return (
      <AutoSizer style={{ width: 270, height: 500 }}>
        {({ width, height }) => (
          <VirtualList
            key={this.state.listKey}
            scrollToIndex={this.state.rowIndex}
            scrollToAlignment="start"
            width={width}
            height={height}
            rowHeight={this.virtualListRef.current.rowHeight}
            deferredMeasurementCache={this.virtualListRef.current}
            rowCount={data.length}
            rowRenderer={({ key, index, style, parent }) => {
              const item = data[index];
              const selected: boolean =
                this.props.selectedItem && this.props.selectedItem.id === item.id;

              const recomputeRows = () => {
                this.handleUpdateRowCache();
                this.setState({
                  listKey: this.state.listKey + 1,
                  rowIndex: index,
                });
              };

              return (
                <CellMeasurer
                  key={key}
                  cache={this.virtualListRef.current}
                  parent={parent}
                  columnIndex={0}
                  rowIndex={index}
                >
                  <div style={style}>
                    <DropdownList
                      key={item.id}
                      location={item}
                      selected={selected}
                      columnName={columnName}
                      onExpandLocationChange={this.handleExpandedLocationChange}
                      expandedLocation={this.expandedLocation}
                      recomputeRows={recomputeRows}
                      onClick={(): void => {
                        if (this.props.index < 5) {
                          this.props.locationUrlConfig.updatedId(
                            this.props.pathChooser,
                            data,
                            item
                          );
                          this.props.onSelect(item, this.props.index);
                        }
                      }}
                      {...this.props}
                    />
                  </div>
                </CellMeasurer>
              );
            }}
          />
        )}
      </AutoSizer>
    );
  }

  renderExpansionPanel(data: Array<*>): React.Element<*> {
    return Object.keys(data[0]).map((currentStatus: string) => {
      const isExpanded: string = this.expandedStatus === currentStatus ? styles.expanded : '';

      return (
        <ExpansionPanel
          key={currentStatus}
          className={`${styles['expansion-panel']} ${isExpanded}`}
          elevation={0}
          expanded={this.expandedStatus === currentStatus}
          onChange={this.handleExpandedChange(currentStatus)}
        >
          {this.renderExpansionPanelSummary(data, currentStatus)}
          {this.expandedStatus === currentStatus &&
            this.renderExpansionPanelDetails(data[0][currentStatus], currentStatus)}
        </ExpansionPanel>
      );
    });
  }

  renderAddNewLink(): React.Element<*> {
    return (
      <AddNewLink
        linkTo=""
        linkText={this.props.linkText}
        action={this.props.action}
        config={this.props.config}
        permissions={createNewLocation}
      />
    );
  }

  render() {
    const { accountStore, blankText, index }: TypeProps = this.props;
    const locationLists: Array<*> = accountStore.getLocationLists;
    const data: Array<*> = this.props.columnData;

    let errorString: boolean;

    if (locationLists.length === index) {
      errorString = typeof locationLists[index - 1] === 'string';
    }

    const noData: boolean = !data || typeof data === 'string' || errorString;
    const selectText: string = locationLists.length === index ? blankText : '';

    const displayText = (
      <div className={styles['finder-column-empty']}>
        <p>{noData ? data : selectText}</p>
      </div>
    );

    if (noData || !data.length) {
      if (accountStore.loadingResources && locationLists.length === index) {
        return <Loading />;
      }

      if (typeof data === 'string' || typeof data === 'undefined') {
        return this.renderAddNewLink();
      }

      return displayText;
    }

    return (
      <List className={styles['expandable-container']} component="div" disablePadding={true}>
        {this.props.expandable ? (
          <React.Fragment>
            {this.props.expandable && this.renderAddNewLink()}
            {data[0] && this.renderExpansionPanel(data)}
          </React.Fragment>
        ) : (
          this.renderExpansionPanelDetails(data)
        )}
      </List>
    );
  }
}
